import React from "react"
import { ListItem, Text, UnorderedList, VStack } from "@chakra-ui/react";

import { mediaLibrary } from "./assets"

export const homePageFeatures = [
  {
    title: "Future-proof media optimization and transformation",
    badge: {
      label: "Deliver",
      color: "imagekitPurple"
    },
    items: [
      {
        title: {
          strong: "Transform images & videos",
          rest: "Ship media-rich experiences faster without maintaining multiple variants with a unified URL-based API that supports 50+ real-time manipulations across images and videos.",
        },
        graphic: {
          videoUrl: "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/Homepage/Transform%20media%20assets%20as%20per%20your%20requirements.mp4?updatedAt=17140511338121&tr=w-1250",
          altText: "Transform media assets as per your requirements"
        }
      },
      {
        title: {
          strong: "Balance visual quality and optimization",
          rest: "Deliver high-quality, lightning-fast experiences with uninterrupted streaming and real-time media optimizations that support the latest formats like AVIF, HEIC, WebM, WebP, and more.",
        },
        graphic: {
          videoUrl: "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/Homepage/Balance%20visual%20quality%20and%20optimization.mp4?updatedAt=17140511314441&tr=w-1250",
          altText: "Balance visual quality and optimization"
        }
      },
      {
        title: {
          strong: "Dynamic media personalization",
          rest: "Unlock the potential to deeply connect with your customers with personalized, dynamic banners and rich product visuals by directly overlaying text, images, and videos from the URL.",
        },
        graphic: {
          videoUrl: "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/Homepage/Dynamically%20build%20unique%20visual%20experiences.mp4?updatedAt=171405112393122&tr=w-1250",
          altText: "Dynamically build unique visual experiences"
        }
      },
      {
        title: {
          strong: "Integrate with your existing setup",
          rest: "Start delivering a world-class media experience in minutes by natively integrating ImageKit with your cloud infrastructure. Our powerful integrations, APIs, and SDKs seamlessly fit into your team's workflow.",
        },
        graphic: {
          videoUrl: "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/Homepage/Integrate%20with%20your%20existing%20setup%20and%20workflow.mp4?updatedAt=17140511298301&tr=w-1250",
          altText: "Integrate with your existing setup and workflow"
        }
      }
    ]
  },
  {
    title: "AI-powered image generation and editing at scale",
    badge: {
      label: "ImageKit AI",
      color: "imagekitBlue"
    },
    items: [
      {
        title: {
          strong: "Create images using text prompts",
          rest: "Generate new visuals by providing text prompts directly in the URL. Combine these with other ImageKit transformations in real time to effortlessly create new assets at scale with minimal effort",
        },
        "graphic": {
          "imageUrl": "https://ik.imagekit.io/ikmedia/Graphics/AI%20Landing%20page/Text%20prompt%20in%20URL.jpg?updatedAt=1726226940145&tr=w-1250",
          "altText": "Create images using text prompts"
        },
        docLink: "https://imagekit.io/docs/ai-transformations#generate-image-via-text-prompt",
        docLabel: "Learn More"
      },
      {
        title: {
          "strong": "Generate image variations",
          "rest": "Start with a base image and generate multiple variations using a simple URL-based transformation parameter. Keep your assets fresh with GenAI built into the image URL"
        },
        "graphic": {
          "videoUrl": "https://ik.imagekit.io/ikmedia/Graphics/AI%20Landing%20page/Create%20new%20images.mp4?updatedAt=17250325000211&tr=w-1250",
          "altText": "Create new image variations with GenAI"
        },
        docLink: "https://imagekit.io/docs/ai-transformations#generate-variations-of-an-image-e-genvar",
        docLabel: "Learn More"
      },
      {
        title: {
          strong: "Change backgrounds with studio-quality realism",
          rest: "Transform your images with AI-generated, studio-quality backgrounds. Perfect for enhancing product photos, portraits, and more with a touch of professional realism."
        },
        "graphic": {
          "videoUrl": "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/Homepage/Background%20change%202.mp4?updatedAt=1733121129435&tr=w-1250",
          "altText": "Change backgrounds with studio-quality realism"
        },
      },
      {
        title: {
          strong: "Expand images with Generative Fill",
          rest: "Set your desired dimensions, and let AI extend the background to adjust your images to any aspect ratio or device, all without the need for cropping or reshoots.",
        },
        graphic: {
          videoUrl: "https://ik.imagekit.io/ikmedia/Graphics/AI%20Landing%20page/Gen%20Fill.mp4?updatedAt=17250225082506&tr=w-1250,so-1,eo-5",
          altText: "Expand images with Generative Fill"
        }
      },
      {
        title: {
          strong: "Upscale low-resolution images",
          rest: "Enhance low-quality legacy images or grainy user-uploaded images into high-resolution, visually stunning assets with AI-driven upscaling and retouching.",
        },
        graphic: {
          imageUrl: "https://ik.imagekit.io/ikmedia/Graphics/AI%20Landing%20page/Upscale.jpg?updatedAt=1724966309208898&tr=w-648,dpr-2.0",
          altText: "Upscale low-resolution images"
        }
      },
      {
        title: {
          strong: "Remove background and add shadows",
          rest: "Easily remove backgrounds, add shadows, retouch product images, and adapt assets to various platform requirements with intuitive URL-based AI transformations.",
        },
        graphic: {
          imageUrl: "https://ik.imagekit.io/ikmedia/Graphics/AI%20Landing%20page/Product%20Images.jpg?updatedAt=17255256268525&tr=w-648,dpr-2",
          altText: "Create stunning product images at scale with AI"
        }
      },
    ]
  },
  {
    title: "Manage your growing storage with the integrated DAM",
    badge: {
      label: "Manage",
      color: "imagekitYellow"
    },
    items: [
      {
        title: {
          strong: "Centralized digital asset storage",
          rest: "From product images and videos to ad graphics, sales decks, promotional banners, and more, ImageKit helps keep your asset repository centralized and accessible as you grow.",
        },
        graphic: {
          videoUrl: "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/Homepage/Central%20Hub.mp4?updatedAt=17140511252821&tr=w-1250",
          altText: "A central hub for all your digital assets"
        }
      },
      {
        title: {
          strong: "Organize assets your way",
          rest: "Use structured metadata with AI-powered tagging to create a customized content organization system suitable for a growing number of digital assets and publishing platforms.",
        },
        graphic: {
          videoUrl: "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/Homepage/Organize%20assets%20your%20way.mp4?updatedAt=17140511305001&tr=w-1250",
          altText: "Organize assets your way"
        }
      },
      {
        title: {
          strong: "Find the right assets quickly with AI",
          rest: "The AI-powered search provides suggestions as you type and finds content visually similar to your input text or image. You can also combine multiple filters to pinpoint the specific assets you need.",
        },
        graphic: {
          videoUrl: "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/Homepage/DAM%20AI%20search.mp4?updatedAt=17140511305001&tr=w-1250",
          altText: "Find the right assets quickly"
        }
      }
    ]
  },
  {
    title: "Simplified cross-team collaboration",
    badge: {
      label: "Collaborate",
      color: "imagekitGreen"
    },
    items: [
      {
        title: {
          strong: "Share assets",
          rest: "Safely share one or more assets with granular access control management and secure public links. Reduce risks associated with sharing via email, chat, or file transfers.",
        },
        graphic: {
          videoUrl: "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/Homepage/Share%20assets%20with%20relevant%20stakeholders.mp4?updatedAt=17140511300261&tr=w-1250",
          altText: "Share assets with relevant stakeholders"
        }
      },
      {
        title: {
          strong: "Speed up collaboration",
          rest: "Collate feedback, track multiple iterations, monitor activity logs and asset history, and publish assets when ready without switching between various channels.",
        },
        graphic: {
          videoUrl: "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/Homepage/Speed%20up%20collaboration%20during%20asset%20creation.mp4?updatedAt=17199824529006&tr=w-1250",
          altText: "Speed up collaboration during asset creation"
        }
      },
      {
        title: {
          strong: "Bring everyone on the same page",
          rest: "Eliminate duplication, maintain brand consistency, and ensure all teams refer to the correct asset version across channels with versioning and virtual Media Collections.",
        },
        graphic: {
          videoUrl: "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/Homepage/Bring%20everyone%20on%20the%20same%20page.mp4?updatedAt=17140511245761&tr=w-1250",
          altText: "Bring everyone on the same page"
        }
      }
    ]
  }
]

export const dynamicAssetCreation = [
  {
    title:
      "Dynamically create images using watermark, text overlay and chained transformation.",
    items: [
      {
        title: {
          strong: "Create multiple variants yourself",
          rest:
            "Use URL parameters to create new images without relying on designers.",
        },
        graphic: {
          imageUrl:
            "https://ik.imgkit.net/ikmedia/building.jpeg?tr=w-500,h-400,fo-auto,dpr-2",
          highlight: "tr=w-500,h-400,fo-auto,dpr-2",
          width: 500,
          docLabel: "Learn basic resizing",
          docLink: "https://docs.imagekit.io/features/image-transformations",
          altText: "Create multiple image variants",
          imageHasShadow: false,
        },
      },
      {
        title: {
          strong: "Generate dynamic banners",
          rest:
            "Customize the image in real-time using text and image overlay.",
        },
        graphic: {
          imageUrl:
            "https://ik.imgkit.net/ikmedia/building.jpeg?tr=w-500,h-400,dpr-2,q-100:l-text,i-Random%20fact%20about%20this%20building,fs-40,ia-left,pa-15,bg-00000070,co-FFFFFFF,lx-40,ly-40,l-end",
          highlight: "",
          width: 500,
          hideDisplayUrl: true,
          showOpenInNewTab: true,
          docLabel: "Learn more about overlay",
          docLink:
            "https://docs.imagekit.io/features/image-transformations/overlay",
          altText: "Generate dynamic banners",
          imageHasShadow: false,
        },
      },
      {
        title: {
          strong: "Create personalized images",
          rest:
            "Get better ROI from your retargeting campaigns using a high degree of personalization in images.",
        },
        graphic: {
          imageUrl:
            "https://ik.imgkit.net/ikmedia/white-canvas.png?tr=w-1000,h-667:l-image,i-site_graphics/girl_in_white_500.jpg,w-500,h-667,lx-0,ly-0,l-end:l-image,i-site_graphics/guy_in_yellow_500.jpg,w-500,h-667,lx-500,ly-0,l-end:l-text,i-HEY%20MIKE%E2%80%9A%20GET%20FLAT%2040%25%20OFF,bg-FFFFFF80,pa-20,fs-30,co-000000,tg-b,r-16,lx-270,ly-550,l-end",
          hideDisplayUrl: true,
          showOpenInNewTab: true,
          highlight: "",
          width: 500,
          docLabel: "Learn how to create a photo collage",
          docLink: "https://imagekit.io/blog/how-to-create-a-photo-collage/",
          altText: "Create personalized images",
          imageHasShadow: false,
        },
      },
    ],
  },
]